/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/sheets/QaPhoneTemplatesSheet.tsx
 * FILE_ID: QA-TEMPLATES-PHONE-001
 * PURPOSE: Phone More-sheet template library using the shared picker hook.
 * LANDMINE: Header and Insert stay shrink-0. Do not touch mode bar or JobQaTab.
 */
import { useEffect, useRef, useState } from 'react'
import { ChevronLeft } from 'lucide-react'
import { MobileSheet } from '@quotech/ui'
import type { JobQaTabController } from '../../useJobQaTabController'
import { addArchivedStructure, type QuickTemplate } from '../../../../qa/archivedStructureTemplates'
import { QaArchivedTemplateList } from '../../templates/QaArchivedTemplateList'
import { QaNewTemplateForm } from '../../templates/QaNewTemplateForm'
import { QaTemplateCreateMenu } from '../../templates/QaTemplateCreateMenu'
import { QaTemplateAddProduct } from '../../templates/QaTemplateAddProduct'
import { QaTemplateCatalogPane } from '../../templates/QaTemplateCatalogPane'
import { QaTemplateContentsPane } from '../../templates/QaTemplateContentsPane'
import { QaTemplateInsertedSummary } from '../../templates/QaTemplateInsertedSummary'
import { QaTemplateSystemsPane } from '../../templates/QaTemplateSystemsPane'
import { QaTemplateTargetPicker } from '../../templates/QaTemplateTargetPicker'
import { useQaTemplatePicker } from '../../templates/useQaTemplatePicker'

const TITLES = {
  types: 'Templates',
  systems: 'Systems',
  detail: 'Checklists',
  areas: 'Insert into',
  inserted: 'Inserted',
} as const

export function QaPhoneTemplatesSheet({
  open,
  onClose,
  controller,
  onInserted,
}: {
  open: boolean
  onClose: () => void
  controller: JobQaTabController
  onInserted?: () => void
}) {
  if (!open) return null
  return <QaPhoneTemplatesSheetBody onClose={onClose} controller={controller} onInserted={onInserted} />
}

function QaPhoneTemplatesSheetBody({
  onClose,
  controller,
  onInserted,
}: {
  onClose: () => void
  controller: JobQaTabController
  onInserted?: () => void
}) {
  const [creating, setCreating] = useState(false)
  const [archived, setArchived] = useState(false)
  const applyArchived = async (template: QuickTemplate) => {
    const inspectionId = controller.workspace.activeInspectionId
    if (!inspectionId) return
    await addArchivedStructure(controller.api.addTable.bind(controller.api), inspectionId, template)
    await controller.workspace.areasQuery.refetch()
    setArchived(false)
  }
  const picker = useQaTemplatePicker(controller.api, {
    ...controller.workspace,
    jobId: controller.props.jobId,
    pendingWriteChoice: controller.setupWriteChoice,
    consumePendingWriteChoice: controller.consumeSetupWrite,
    intoChecklist: controller.templatesInto,
  })
  const restoreDefaults = () => {
    if (!window.confirm('Remove this job\'s custom checklists from the template list? QA already added stays.')) return
    void controller.api.restoreDefaults(controller.props.jobId).then(() => picker.reload())
  }
  const told = useRef(false)
  useEffect(() => {
    if (picker.step === 'inserted' && !told.current) {
      told.current = true
      onInserted?.()
    }
  }, [onInserted, picker.step])
  const back = () => {
    if (picker.step === 'systems') picker.setStep('types')
    else if (picker.step === 'detail') picker.setStep('systems')
    else if (picker.step === 'areas') picker.setStep('detail')
    else if (picker.step === 'inserted') picker.reset()
  }
  return (
    <MobileSheet
      open
      size="full"
      onClose={onClose}
      title={TITLES[picker.step]}
      sheetTestId="qa-phone-templates-sheet"
      footer={creating || picker.step === 'inserted' ? undefined : (
        <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">
        {controller.templatesInto
          ? `Adding steps to: ${controller.templatesInto.name}`
          : `Insert into ${(picker.insertNames.length ? picker.insertNames : picker.basketLocationLabels).join(', ')} ${picker.basketCounts.checklists} checklists - ${picker.basketCounts.steps} steps`}
      </p>
          <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => picker.setStep('areas')}
            className="min-h-[44px] flex-1 rounded-md border border-border px-3 text-sm"
          >
            Locations - {picker.targetAreaIds.size + picker.pendingAreaNames.length}
          </button>
          <button
            type="button"
            data-testid="qa-templates-insert"
            disabled={!picker.canInsert || Boolean(picker.detail && picker.detail.sections.length === 0)}
            onClick={() => void picker.insert()}
            className="min-h-[44px] flex-1 rounded-md bg-foreground px-3 text-sm text-background disabled:opacity-40"
          >
            {picker.busy ? 'Inserting...' : 'Insert'}
          </button>
          </div>
        </div>
      )}
    >
      <div className="flex shrink-0 flex-wrap items-center gap-2 px-3 pt-2">
        <QaTemplateCreateMenu
          phone
          onNew={() => { setArchived(false); setCreating(true) }}
          onArchived={() => { setCreating(false); setArchived(true) }}
        />
        {!creating ? (
          <QaTemplateAddProduct
            layout="inline"
            client={controller.client}
            focus={picker.productFocus}
            draft={picker.productDraft}
            locationType={picker.locationType}
            onDraft={picker.setProductDraft}
          />
        ) : null}
        <button
          type="button"
          onClick={restoreDefaults}
          className="ml-auto text-xs font-medium text-primary hover:underline"
        >
          Restore defaults
        </button>
      </div>
      {picker.step !== 'types' ? (
        <button
          type="button"
          onClick={back}
          className="flex min-h-[44px] w-full items-center gap-2 px-3 text-sm"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Back
        </button>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {archived && !creating ? (
          <QaArchivedTemplateList onPick={(template) => void applyArchived(template)} />
        ) : creating ? (
          <QaNewTemplateForm
            api={controller.api}
            jobId={controller.props.jobId}
            locationType={picker.locationType}
            onCancel={() => setCreating(false)}
            onCreated={(created) => {
              setCreating(false)
              void picker.reload().then(() => {
                picker.pickLocation(created.location_type)
                picker.pickTemplate(created.id)
              })
            }}
          />
        ) : null}
        {!creating && !archived && picker.step !== 'areas' && picker.step !== 'inserted' ? (
          <div className="flex flex-col gap-4">
            <QaTemplateCatalogPane picker={picker} collapsible />
            <QaTemplateSystemsPane picker={picker} collapsible />
            <QaTemplateContentsPane picker={picker} collapsible />
          </div>
        ) : null}
        {!creating && !archived && picker.step === 'areas' ? <QaTemplateTargetPicker picker={picker} uncapped /> : null}
        {!creating && !archived && picker.step === 'inserted' ? (
          <QaTemplateInsertedSummary picker={picker} onClose={onClose} onAgain={picker.reset} />
        ) : null}
      </div>
    </MobileSheet>
  )
}
